import React, { useState, useEffect, useCallback } from 'react';

/* ─── Platform logo component ────────────────────────────────────── */
function PlatformLogo({ logoUrl, emoji, name, size = 36 }) {
  const [failed, setFailed] = useState(!logoUrl);
  if (failed) return <span style={{ fontSize: size * 0.8, lineHeight: 1 }}>{emoji}</span>;
  return (
    <img
      src={logoUrl}
      alt={name}
      width={size}
      height={size}
      style={{ borderRadius: 8, objectFit: 'contain', display: 'block' }}
      onError={() => setFailed(true)}
    />
  );
}

/* ─── Keyframe injection ─────────────────────────────────────────── */
const WIZARD_CSS = `
  .wz-enter-forward {
    animation: wzSlideInRight 0.35s cubic-bezier(.4,0,.2,1) both;
  }
  .wz-enter-backward {
    animation: wzSlideInLeft 0.35s cubic-bezier(.4,0,.2,1) both;
  }
  @keyframes wzSlideInRight {
    from { opacity: 0; transform: translateX(48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes wzSlideInLeft {
    from { opacity: 0; transform: translateX(-48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .topic-pill { transition: all 0.15s cubic-bezier(.4,0,.2,1); }
  .topic-pill:hover { transform: translateY(-2px) scale(1.03); }
  .platform-card { transition: all 0.18s cubic-bezier(.4,0,.2,1); }
  .platform-card:hover:not([disabled]) { transform: translateY(-3px); }
  .time-card { transition: all 0.15s cubic-bezier(.4,0,.2,1); }
  .time-card:hover { transform: translateY(-2px); }
  .wz-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(14,165,233,0.45); }
  .wz-btn-primary { transition: all 0.18s cubic-bezier(.4,0,.2,1); }
  .wz-input:focus { border-color: #0ea5e9 !important; box-shadow: 0 0 0 3px rgba(14,165,233,0.18) !important; outline: none; }
  @keyframes progressFill {
    from { width: 0%; }
  }
`;

/* ─── Data ───────────────────────────────────────────────────────── */
const TOPIC_GROUPS = [
  {
    label: 'Finance & Business',
    color: '#22c55e',
    topics: [
      { name: 'Finance',     emoji: '💰' },
      { name: 'Crypto',      emoji: '₿'  },
      { name: 'Business',    emoji: '📈' },
      { name: 'Startups',    emoji: '🦄' },
      { name: 'Real Estate', emoji: '🏠' },
    ],
  },
  {
    label: 'Health & Wellness',
    color: '#f43f5e',
    topics: [
      { name: 'Health',          emoji: '🏥' },
      { name: 'Fitness',         emoji: '💪' },
      { name: 'Mental Health',   emoji: '🧠' },
      { name: 'Nutrition',       emoji: '🥗' },
      { name: 'Self-Improvement',emoji: '🌱' },
      { name: 'Parenting',       emoji: '👶' },
    ],
  },
  {
    label: 'Tech & Science',
    color: '#6366f1',
    topics: [
      { name: 'AI',         emoji: '🤖' },
      { name: 'Tech',       emoji: '💻' },
      { name: 'Science',    emoji: '🔬' },
      { name: 'Space',      emoji: '🚀' },
      { name: 'Gaming',     emoji: '🎮' },
      { name: 'Automotive', emoji: '🚗' },
    ],
  },
  {
    label: 'Culture & Lifestyle',
    color: '#f97316',
    topics: [
      { name: 'Entertainment', emoji: '🎬' },
      { name: 'Sports',        emoji: '🏆' },
      { name: 'Travel',        emoji: '✈️' },
      { name: 'Food',          emoji: '🍕' },
      { name: 'Design',        emoji: '🎨' },
      { name: 'Fashion',       emoji: '👗' },
      { name: 'Music',         emoji: '🎵' },
    ],
  },
  {
    label: 'Knowledge & World',
    color: '#a855f7',
    topics: [
      { name: 'Philosophy',    emoji: '📚' },
      { name: 'World News',    emoji: '🗞️' },
      { name: 'Climate',       emoji: '🌍' },
      { name: 'Productivity',  emoji: '⚡' },
      { name: 'History',       emoji: '🏛️' },
      { name: 'Photography',   emoji: '📷' },
    ],
  },
];

const ALL_TOPICS = TOPIC_GROUPS.flatMap(g => g.topics.map(t => ({ ...t, color: g.color })));

const MAX_TOPICS = 6;

const PLATFORMS = [
  {
    name: 'YouTube',
    icon: '▶️',
    logo: 'https://cdn.simpleicons.org/youtube/FF0000',
    desc: 'Most recent videos on your exact topics',
    active: true,
  },
  {
    name: 'News',
    icon: '📰',
    logo: 'https://www.google.com/s2/favicons?domain=newsapi.org&sz=64',
    desc: 'Breaking headlines from 80,000+ global news sources',
    active: true,
  },
  {
    name: 'Guardian',
    icon: '🔵',
    logo: 'https://cdn.simpleicons.org/theguardian/6fbfff',
    desc: 'In-depth journalism from The Guardian',
    active: true,
  },
  {
    name: 'Hacker News',
    icon: '🟠',
    logo: 'https://cdn.simpleicons.org/ycombinator/FF6600',
    desc: 'Top tech & startup stories, filtered to your interests',
    active: true,
  },
  {
    name: 'NASA',
    icon: '🌌',
    logo: 'https://cdn.simpleicons.org/nasa/E03C31',
    desc: 'APOD, image library & science content from NASA',
    active: true,
  },
  {
    name: 'Alpha Vantage',
    icon: '📊',
    logo: 'https://www.google.com/s2/favicons?domain=alphavantage.co&sz=64',
    desc: 'Real-time financial news, market sentiment & economic data',
    active: true,
  },
  {
    name: 'Currents',
    icon: '⚡',
    logo: 'https://www.google.com/s2/favicons?domain=currentsapi.services&sz=64',
    desc: 'Up-to-the-minute news across business, tech & world events',
    active: true,
  },
  {
    name: 'GNews',
    icon: '🌐',
    logo: 'https://www.google.com/s2/favicons?domain=gnews.io&sz=64',
    desc: 'Global headlines curated for business, startups & world news',
    active: true,
  },
  {
    name: 'USDA',
    icon: '🥗',
    logo: 'https://www.google.com/s2/favicons?domain=usda.gov&sz=64',
    desc: 'Official U.S. nutrition & food composition data spotlights',
    active: true,
  },
  {
    name: 'Reddit',
    icon: '🔴',
    logo: 'https://cdn.simpleicons.org/reddit/FF4500',
    desc: 'Top upvoted posts from curated subreddits',
    active: false,
    soon: true,
  },
  {
    name: 'X / Twitter',
    icon: '✕',
    logo: 'https://cdn.simpleicons.org/x/e2e8f0',
    desc: 'Trending threads from top creators & thinkers',
    active: false,
    soon: true,
  },
];

const TIME_PRESETS = [
  { label: '6:00 AM', value: '06:00', icon: '🌅', desc: 'Early bird' },
  { label: '7:00 AM', value: '07:00', icon: '☀️', desc: 'Morning start' },
  { label: '8:00 AM', value: '08:00', icon: '☕', desc: 'Most popular', badge: true },
  { label: '9:00 AM', value: '09:00', icon: '🚀', desc: 'After coffee' },
  { label: '12:00 PM', value: '12:00', icon: '🌤️', desc: 'Lunch break' },
  { label: '6:00 PM', value: '18:00', icon: '🌆', desc: 'End of day' },
  { label: '9:00 PM', value: '21:00', icon: '🌙', desc: 'Evening wind-down' },
  { label: 'Custom', value: 'custom', icon: '🎛️', desc: 'Pick your own time' },
];

const TIMEZONES = [
  { label: 'Eastern Time (ET)',   value: 'America/New_York',      abbr: 'ET'  },
  { label: 'Central Time (CT)',   value: 'America/Chicago',       abbr: 'CT'  },
  { label: 'Mountain Time (MT)',  value: 'America/Denver',        abbr: 'MT'  },
  { label: 'Pacific Time (PT)',   value: 'America/Los_Angeles',   abbr: 'PT'  },
  { label: 'Alaska Time (AKT)',   value: 'America/Anchorage',     abbr: 'AKT' },
  { label: 'Hawaii Time (HT)',    value: 'Pacific/Honolulu',      abbr: 'HT'  },
  { label: 'London (GMT/BST)',    value: 'Europe/London',         abbr: 'GMT' },
  { label: 'Paris / Berlin (CET)',value: 'Europe/Paris',          abbr: 'CET' },
  { label: 'Dubai (GST)',         value: 'Asia/Dubai',            abbr: 'GST' },
  { label: 'Mumbai (IST)',        value: 'Asia/Kolkata',          abbr: 'IST' },
  { label: 'Singapore (SGT)',     value: 'Asia/Singapore',        abbr: 'SGT' },
  { label: 'Tokyo (JST)',         value: 'Asia/Tokyo',            abbr: 'JST' },
  { label: 'Sydney (AEST)',       value: 'Australia/Sydney',      abbr: 'AEST'},
  { label: 'São Paulo (BRT)',     value: 'America/Sao_Paulo',     abbr: 'BRT' },
  { label: 'Mexico City (CST)',   value: 'America/Mexico_City',   abbr: 'CST' },
  { label: 'Toronto (ET)',        value: 'America/Toronto',       abbr: 'ET'  },
  { label: 'Vancouver (PT)',      value: 'America/Vancouver',     abbr: 'PT'  },
];

function detectTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
}

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: '$5',
    period: '/mo',
    links: 5,
    desc: 'The daily essential — 5 hand-picked links every day.',
    features: ['5 links per day', 'All 30 topics', 'Email delivery', 'Custom schedule'],
    color: '#3b82f6',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$8',
    period: '/mo',
    links: 10,
    desc: 'Double the signal — 10 curated links for the power reader.',
    features: ['10 links per day', 'All 30 topics', 'Email delivery', 'Custom schedule', 'Priority sources'],
    color: '#7c3aed',
    badge: 'Best value',
  },
];

const STEPS = [
  { id: 'name',    title: (name) => 'What should we call you?',             sub: () => 'We\'ll use this to personalize your digest.' },
  { id: 'topics',  title: (name) => `What are you into, ${name || 'you'}?`, sub: () => 'Pick up to 6 topics. You can change them anytime.' },
  { id: 'time',    title: () => 'When do you want your droplet?',           sub: () => 'Pick your time zone and delivery time.' },
  { id: 'plan',    title: () => 'Choose your plan',                         sub: () => 'Start reading smarter today.' },
  { id: 'contact', title: (name) => `Almost there, ${name || 'friend'}!`,  sub: () => 'Enter your email and choose how you want your digest.' },
  { id: 'verify',  title: () => 'Check your inbox',                         sub: () => 'Enter the 6-digit code we just emailed you.' },
];

/* ─── Wizard ─────────────────────────────────────────────────────── */
export default function SignupWizard({ onSuccess, onBack }) {
  const [step, setStep]           = useState(0);
  const [direction, setDirection] = useState('forward');
  const [animKey, setAnimKey]     = useState(0);

  // Form state
  const [name, setName]               = useState('');
  const [topics, setTopics]           = useState([]);
  const [platforms, setPlatforms]     = useState(['YouTube', 'News', 'Guardian', 'Hacker News', 'NASA', 'Alpha Vantage', 'Currents', 'GNews', 'USDA']);
  const [deliveryTime, setDeliveryTime] = useState('08:00');
  const [customTime, setCustomTime]   = useState('');
  const [timezone, setTimezone]         = useState(detectTimezone);
  const [email, setEmail]               = useState('');
  const [phone, setPhone]               = useState('');
  const [deliveryChannel, setDeliveryChannel] = useState('email');
  const [plan, setPlan]                 = useState(null); // 'basic' | 'pro'
  const [promoCode, setPromoCode]       = useState('');
  const [promoValid, setPromoValid]     = useState(false);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError]     = useState('');
  const [showPromo, setShowPromo]       = useState(false);
  const [otp, setOtp]                   = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const totalSteps = STEPS.length;
  const progress = ((step) / (totalSteps - 1)) * 100;

  function goNext() {
    setError('');
    setDirection('forward');
    setAnimKey(k => k + 1);
    setStep(s => s + 1);
  }

  function goBack() {
    setError('');
    if (step === 0) { onBack(); return; }
    setDirection('backward');
    setAnimKey(k => k + 1);
    setStep(s => s - 1);
  }

  function toggleTopic(name) {
    setTopics(prev => {
      if (prev.includes(name)) return prev.filter(t => t !== name);
      if (prev.length >= MAX_TOPICS) return prev; // cap at 6
      return [...prev, name];
    });
  }

  function togglePlatform(name) {
    setPlatforms(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]
    );
  }

  function selectAllInGroup(group) {
    const names = group.topics.map(t => t.name);
    const allSelected = names.every(n => topics.includes(n));
    if (allSelected) {
      setTopics(prev => prev.filter(t => !names.includes(t)));
    } else {
      setTopics(prev => {
        const toAdd = names.filter(n => !prev.includes(n));
        const slots = MAX_TOPICS - prev.length;
        return [...new Set([...prev, ...toAdd.slice(0, slots)])];
      });
    }
  }

  function canContinue() {
    if (step === 0) return name.trim().length >= 2;
    if (step === 1) return topics.length >= 1;
    if (step === 2) return true; // time — always valid
    if (step === 3) return plan !== null; // plan selected
    if (step === 4) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (step === 5) return otp.trim().length === 6;
    return false;
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && canContinue() && step < totalSteps - 1) goNext();
  }

  // Step 4: send OTP to email
  async function handleSendOTP() {
    setError('');
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/subscribe/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to send code.'); return; }
      goNext();
    } catch {
      setError('Unable to connect. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }

  // Step 5: verify OTP then subscribe
  async function handleVerifyAndSubscribe() {
    setError('');
    const normalizedEmail = email.trim().toLowerCase();
    const finalTime = deliveryTime === 'custom' ? customTime || '08:00' : deliveryTime;
    const resolvedTZ = TIMEZONES.find(t => t.value === timezone) ? timezone : 'America/New_York';

    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';

      // 1. Verify OTP
      const verifyRes = await fetch(`${apiBase}/api/subscribe/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, otp }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) { setError(verifyData.error || 'Invalid code.'); return; }
      const token = verifyData.token;

      // 2. Create subscription
      const subRes = await fetch(`${apiBase}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizedEmail,
          phone: deliveryChannel === 'sms' ? '+1' + phone.replace(/\D/g, '').slice(-10) : undefined,
          name: name.trim(),
          topics,
          platforms,
          delivery_time: finalTime,
          timezone: resolvedTZ,
          delivery_channel: deliveryChannel,
          verification_token: token,
          tier: plan,
          promo_code: promoValid ? promoCode.trim() : undefined,
        }),
      });
      const subData = await subRes.json();
      if (!subRes.ok) { setError(subData.error || 'Something went wrong.'); return; }

      // Paid plan without promo — redirect to Stripe checkout
      if (subData.requiresPayment && subData.checkoutUrl) {
        window.location.href = subData.checkoutUrl;
        return;
      }

      onSuccess({ email: normalizedEmail, name: name.trim(), deliveryTime: finalTime, timezone: resolvedTZ, plan });
    } catch {
      setError('Unable to connect. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const currentStep = STEPS[step];
  const animClass = direction === 'forward' ? 'wz-enter-forward' : 'wz-enter-backward';

  return (
    <>
      <style>{WIZARD_CSS}</style>
      <div style={w.page}>

        {/* Top bar */}
        <div style={w.topBar}>
          <button onClick={goBack} style={w.backBtn}>
            ← {step === 0 ? 'Home' : 'Back'}
          </button>
          <div style={w.stepIndicator}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                ...w.dot,
                background: i <= step ? '#0ea5e9' : '#1e3a5f',
                width: i === step ? 20 : 8,
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>
          <div style={w.stepCount}>
            {step + 1} <span style={{ color: '#334155' }}>/ {totalSteps}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div style={w.progressTrack}>
          <div style={{ ...w.progressFill, width: `${progress}%`, transition: 'width 0.4s ease' }} />
        </div>

        {/* Step content */}
        <div style={w.body}>
          <div key={animKey} className={animClass} style={w.stepWrap}>

            {/* Step header */}
            <div style={w.stepHeader}>
              <h1 style={w.stepTitle}>{currentStep.title(name)}</h1>
              <p style={w.stepSub}>{currentStep.sub()}</p>
            </div>

            {/* ── STEP 0: Name ── */}
            {step === 0 && (
              <div style={w.fieldGroup}>
                <input
                  className="wz-input"
                  autoFocus
                  type="text"
                  placeholder="Your first name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={40}
                  style={w.bigInput}
                />
                {name.length > 0 && (
                  <div style={w.inputHint}>
                    Nice to meet you, <strong style={{ color: '#38bdf8' }}>{name}</strong> 👋
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 1: Topics ── */}
            {step === 1 && (
              <div>
                <div style={w.topicStats}>
                  <span style={{ ...w.topicCount, color: topics.length >= MAX_TOPICS ? '#f59e0b' : '#38bdf8' }}>
                    {topics.length} / {MAX_TOPICS} selected
                  </span>
                  {topics.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTopics([])}
                      style={w.selectAllBtn}
                    >
                      Clear all
                    </button>
                  )}
                </div>
                {topics.length >= MAX_TOPICS && (
                  <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#fbbf24' }}>
                    ✓ You've picked 6 topics — that's the max. You can always update them later in your profile.
                  </div>
                )}

                {TOPIC_GROUPS.map(group => (
                  <div key={group.label} style={w.topicGroup}>
                    <div style={w.topicGroupHeader}>
                      <span style={{ ...w.groupDot, background: group.color }} />
                      <span style={w.groupLabel}>{group.label}</span>
                    </div>
                    <div style={w.topicRow}>
                      {group.topics.map(({ name: tName, emoji }) => {
                        const sel = topics.includes(tName);
                        const atCap = !sel && topics.length >= MAX_TOPICS;
                        return (
                          <button
                            key={tName}
                            type="button"
                            className="topic-pill"
                            onClick={() => { if (!atCap) toggleTopic(tName); }}
                            disabled={atCap}
                            style={{
                              ...w.topicPill,
                              background: sel ? group.color + '22' : '#0a1628',
                              border: `1.5px solid ${sel ? group.color : atCap ? '#1e293b' : '#1e3a5f'}`,
                              color: sel ? group.color : atCap ? '#1e3a5f' : '#64748b',
                              opacity: atCap ? 0.35 : 1,
                              cursor: atCap ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <span>{emoji}</span>
                            <span>{tName}</span>
                            {sel && <span style={{ fontSize: 10, fontWeight: 700 }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── STEP 2: Time ── */}
            {step === 2 && (
              <div>
                <div style={w.timeGrid}>
                  {TIME_PRESETS.map(preset => {
                    const sel = deliveryTime === preset.value;
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        className="time-card"
                        onClick={() => setDeliveryTime(preset.value)}
                        style={{
                          ...w.timeCard,
                          background: sel ? 'rgba(14,165,233,0.12)' : '#0a1628',
                          border: `1.5px solid ${sel ? '#0ea5e9' : '#1e3a5f'}`,
                        }}
                      >
                        {preset.badge && (
                          <div style={w.popularBadge}>⭐ Popular</div>
                        )}
                        <div style={w.timeIcon}>{preset.icon}</div>
                        <div style={{ ...w.timeLabel, color: sel ? '#38bdf8' : '#f1f5f9' }}>
                          {preset.label}
                        </div>
                        <div style={w.timeDesc}>{preset.desc}</div>
                        {sel && preset.value !== 'custom' && (
                          <div style={w.timeCheck}>✓</div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {deliveryTime === 'custom' && (
                  <div style={w.customTimeWrap}>
                    <label style={w.customTimeLabel}>Pick your exact time</label>
                    <input
                      className="wz-input"
                      type="time"
                      value={customTime}
                      onChange={e => setCustomTime(e.target.value)}
                      style={w.timeInput}
                    />
                  </div>
                )}

                {/* Timezone picker */}
                <div style={{ marginTop: 24 }}>
                  <label style={w.tzLabel}>Your time zone</label>
                  <div style={w.tzGrid}>
                    {TIMEZONES.map(tz => {
                      const sel = timezone === tz.value;
                      return (
                        <button
                          key={tz.value}
                          type="button"
                          onClick={() => setTimezone(tz.value)}
                          style={{
                            ...w.tzBtn,
                            background: sel ? 'rgba(14,165,233,0.12)' : '#0a1628',
                            border: `1.5px solid ${sel ? '#0ea5e9' : '#1e3a5f'}`,
                            color: sel ? '#38bdf8' : '#64748b',
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 11 }}>{tz.abbr}</span>
                          <span style={{ fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>{tz.label.split(' (')[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 5: OTP Verify ── */}
            {step === 5 && (
              <div style={w.phoneStep}>
                <div style={w.otpInfoBox}>
                  <div style={w.otpInfoIcon}>📧</div>
                  <div>
                    <div style={w.otpInfoTitle}>Code sent to <span style={{ color: '#38bdf8' }}>{email.trim()}</span></div>
                    <div style={w.otpInfoSub}>Check your inbox (and spam folder) — expires in 10 minutes.</div>
                  </div>
                </div>

                <div style={{
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.25)',
                  borderRadius: 12,
                  padding: '14px 16px',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  marginBottom: 4,
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>Heads up about your first droplet</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
                      Your first daily droplet has a good chance of landing in spam. To make sure it reaches you, add <strong style={{ color: '#f1f5f9' }}>digest@dailydroplet.org</strong> to your contacts now.
                    </div>
                  </div>
                </div>

                <div style={w.fieldGroup}>
                  <label style={w.phoneLabel}>Enter 6-digit code</label>
                  <input
                    className="wz-input"
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="000000"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={e => e.key === 'Enter' && canContinue() && handleVerifyAndSubscribe()}
                    style={{ ...w.bigInput, letterSpacing: 10, textAlign: 'center', fontSize: 28 }}
                  />
                </div>

                {error && <div style={w.errorBox}>{error}</div>}

                <button
                  type="button"
                  className="wz-btn-primary"
                  onClick={handleVerifyAndSubscribe}
                  disabled={loading || !canContinue()}
                  style={{
                    ...w.submitBtn,
                    opacity: loading || !canContinue() ? 0.45 : 1,
                    cursor: loading || !canContinue() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                      <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span>
                      Verifying…
                    </span>
                  ) : '💧 Verify & start my digest'}
                </button>

                <p style={{ ...w.legal, color: '#334155' }}>
                  Didn't get it?{' '}
                  <button
                    type="button"
                    onClick={() => { setOtp(''); setError(''); goBack(); }}
                    style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, textDecoration: 'underline' }}
                  >
                    Resend code
                  </button>
                </p>
              </div>
            )}

            {/* ── STEP 3: Plan Selection ── */}
            {step === 3 && (
              <div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
                  {PLANS.map(p => {
                    const sel = plan === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlan(p.id)}
                        style={{
                          flex: 1, minWidth: 220, textAlign: 'left', borderRadius: 16,
                          padding: '22px 20px', fontFamily: 'inherit', cursor: 'pointer',
                          background: sel ? `${p.color}18` : '#0a1628',
                          border: `2px solid ${sel ? p.color : '#1e3a5f'}`,
                          position: 'relative', transition: 'all 0.15s',
                        }}
                      >
                        {p.badge && (
                          <div style={{ position: 'absolute', top: 12, right: 12, background: p.color, color: '#fff', borderRadius: 6, padding: '2px 9px', fontSize: 10, fontWeight: 700 }}>{p.badge}</div>
                        )}
                        {sel && (
                          <div style={{ position: 'absolute', top: 12, left: 12, width: 18, height: 18, borderRadius: '50%', background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 900 }}>✓</div>
                        )}
                        <div style={{ fontSize: 28, fontWeight: 800, color: sel ? p.color : '#f1f5f9', fontFamily: 'inherit', marginBottom: 2, marginTop: sel ? 0 : 0 }}>
                          {p.price}<span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>{p.period}</span>
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: sel ? p.color : '#f1f5f9', marginBottom: 6 }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginBottom: 12 }}>{p.desc}</div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          {p.features.map(f => (
                            <li key={f} style={{ fontSize: 12, color: sel ? '#94a3b8' : '#334155', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: p.color, fontSize: 10, fontWeight: 900 }}>✓</span> {f}
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>

                {/* Promo code */}
                {!showPromo ? (
                  <button type="button" onClick={() => setShowPromo(true)} style={{ background: 'none', border: 'none', color: '#475569', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 }}>
                    Have a promo code?
                  </button>
                ) : (
                  <div style={{ marginTop: 4 }}>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 8 }}>Promo code</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className="wz-input"
                        type="text"
                        placeholder="XXXXXXXX"
                        value={promoCode}
                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoValid(false); setPromoError(''); }}
                        style={{ ...w.bigInput, fontSize: 15, letterSpacing: 2, flex: 1, padding: '11px 14px' }}
                        disabled={promoValid}
                      />
                      {!promoValid && (
                        <button
                          type="button"
                          disabled={promoChecking || !promoCode.trim()}
                          onClick={async () => {
                            setPromoChecking(true); setPromoError('');
                            try {
                              const r = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/subscribe/validate-promo`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ code: promoCode.trim() }),
                              });
                              const d = await r.json();
                              if (d.valid) { setPromoValid(true); }
                              else { setPromoError('Invalid promo code.'); }
                            } catch { setPromoError('Could not verify code.'); }
                            finally { setPromoChecking(false); }
                          }}
                          style={{ padding: '11px 18px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {promoChecking ? '…' : 'Apply'}
                        </button>
                      )}
                    </div>
                    {promoValid && (
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, color: '#22c55e', fontSize: 13, fontWeight: 600 }}>
                        <span>✓</span> Promo applied — {plan ? PLANS.find(p => p.id === plan)?.name || plan : 'selected plan'} is free!
                      </div>
                    )}
                    {promoError && <div style={{ marginTop: 6, color: '#f87171', fontSize: 12 }}>{promoError}</div>}
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 4: Contact + channel ── */}
            {step === 4 && (
              <div style={w.phoneStep}>
                {/* Summary recap */}
                <div style={w.summaryCard}>
                  <div style={w.summaryRow}>
                    <span style={w.summaryIcon}>👤</span>
                    <span style={w.summaryVal}>{name}</span>
                  </div>
                  <div style={w.summaryRow}>
                    <span style={w.summaryIcon}>🏷️</span>
                    <span style={w.summaryVal}>
                      {topics.length} topic{topics.length !== 1 ? 's' : ''} · {plan ? (plan === 'pro' ? '10 links/day' : '5 links/day') : 'all sources'}
                    </span>
                  </div>
                  <div style={w.summaryRow}>
                    <span style={w.summaryIcon}>⏰</span>
                    <span style={w.summaryVal}>
                      Daily at {formatDisplayTime(deliveryTime === 'custom' ? customTime : deliveryTime)}
                    </span>
                  </div>
                </div>

                {/* Email input */}
                <div style={w.fieldGroup}>
                  <label style={w.phoneLabel}>Your email address</label>
                  <input
                    className="wz-input"
                    autoFocus
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && step === 4 && canContinue() && handleSendOTP()}
                    style={w.bigInput}
                  />
                  <p style={w.phoneHint}>We'll send your daily digest and a verification code here.</p>
                </div>

                {/* Delivery channel picker */}
                <div>
                  <label style={{ ...w.phoneLabel, display: 'block', marginBottom: 12 }}>Delivery method</label>
                  <div style={{ display: 'flex', gap: 12 }}>
                    {/* Email — active */}
                    <button
                      type="button"
                      onClick={() => setDeliveryChannel('email')}
                      style={{
                        flex: 1, borderRadius: 14, padding: '18px 16px', textAlign: 'left',
                        fontFamily: 'inherit', cursor: 'pointer',
                        background: deliveryChannel === 'email' ? 'rgba(14,165,233,0.12)' : '#0a1628',
                        border: `1.5px solid ${deliveryChannel === 'email' ? '#0ea5e9' : '#1e3a5f'}`,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>📧</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: deliveryChannel === 'email' ? '#38bdf8' : '#f1f5f9', marginBottom: 4 }}>Email</div>
                      <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>Daily digest delivered to your inbox</div>
                      {deliveryChannel === 'email' && (
                        <div style={{ marginTop: 8, display: 'inline-block', background: '#0ea5e9', color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 10, fontWeight: 700 }}>✓ Selected</div>
                      )}
                    </button>

                    {/* SMS — coming soon */}
                    <button
                      type="button"
                      disabled
                      style={{
                        flex: 1, borderRadius: 14, padding: '18px 16px', textAlign: 'left',
                        fontFamily: 'inherit', cursor: 'not-allowed',
                        background: '#0a1628', border: '1.5px solid #1e293b',
                        opacity: 0.5, position: 'relative',
                      }}
                    >
                      <div style={{ position: 'absolute', top: 10, right: 12, background: '#1e3a5f', color: '#475569', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>Coming soon</div>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>📱</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>SMS Text</div>
                      <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.4 }}>Digest sent to your phone</div>
                    </button>
                  </div>
                </div>

                {error && <div style={w.errorBox}>{error}</div>}

                <button
                  type="button"
                  className="wz-btn-primary"
                  onClick={handleSendOTP}
                  disabled={loading || !canContinue()}
                  style={{
                    ...w.submitBtn,
                    opacity: loading || !canContinue() ? 0.45 : 1,
                    cursor: loading || !canContinue() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                      <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span>
                      Sending code…
                    </span>
                  ) : '📧 Send verification code'}
                </button>

                <p style={w.legal}>
                  By subscribing you agree to our{' '}
                  <a href="/terms" onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/terms'); window.dispatchEvent(new PopStateEvent('popstate')); }} style={{ color: '#38bdf8', textDecoration: 'none' }}>Terms</a>
                  {' '}and{' '}
                  <a href="/privacy" onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/privacy'); window.dispatchEvent(new PopStateEvent('popstate')); }} style={{ color: '#38bdf8', textDecoration: 'none' }}>Privacy Policy</a>.
                  Unsubscribe anytime from your account settings.
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Bottom nav (all steps except last) */}
        {step < totalSteps - 1 && (
          <div style={w.bottomBar}>
            {step === 1 && topics.length > 0 && (
              <div style={w.selectedPreview}>
                {topics.slice(0, 5).map(t => {
                  const found = ALL_TOPICS.find(tp => tp.name === t);
                  return (
                    <span key={t} style={{ ...w.previewPill, borderColor: found?.color + '55', color: found?.color }}>
                      {found?.emoji} {t}
                    </span>
                  );
                })}
                {topics.length > 5 && (
                  <span style={w.moreCount}>+{topics.length - 5} more</span>
                )}
              </div>
            )}
            <button
              type="button"
              className="wz-btn-primary"
              onClick={goNext}
              disabled={!canContinue()}
              style={{
                ...w.nextBtn,
                opacity: canContinue() ? 1 : 0.35,
                cursor: canContinue() ? 'pointer' : 'not-allowed',
              }}
            >
              {step === 3 ? 'Enter your email →' : 'Continue →'}
            </button>
          </div>
        )}

      </div>
    </>
  );
}

function formatDisplayTime(val) {
  if (!val) return '8:00 AM';
  const [hh, mm] = val.split(':');
  const h = parseInt(hh, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:${mm} ${suffix}`;
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const w = {
  page: {
    minHeight: '100vh',
    background: '#060d1f',
    display: 'flex',
    flexDirection: 'column',
    color: '#f1f5f9',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: '1px solid #0d1829',
  },
  backBtn: {
    background: 'none', border: 'none', color: '#64748b',
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
    padding: '6px 0',
    transition: 'color 0.15s',
  },
  stepIndicator: { display: 'flex', alignItems: 'center', gap: 6 },
  dot: { height: 8, borderRadius: 4 },
  stepCount: { fontSize: 13, fontWeight: 600, color: '#475569', minWidth: 40, textAlign: 'right' },
  progressTrack: { height: 3, background: '#0d1829' },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #0ea5e9, #6366f1)',
    borderRadius: 2,
  },
  body: {
    flex: 1, overflowY: 'auto',
    padding: 'clamp(24px, 5vw, 56px) clamp(16px, 8vw, 0px)',
    display: 'flex', justifyContent: 'center',
  },
  stepWrap: { width: '100%', maxWidth: 680, padding: '0 24px 120px' },
  stepHeader: { marginBottom: 40 },
  stepTitle: {
    fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 800,
    lineHeight: 1.2, color: '#f8fafc', marginBottom: 12,
  },
  stepSub: { fontSize: 16, color: '#64748b', lineHeight: 1.6 },

  /* Name step */
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 12 },
  bigInput: {
    width: '100%',
    padding: '16px 20px', fontSize: 20, fontWeight: 500,
    background: '#0a1628', border: '1.5px solid #1e3a5f',
    borderRadius: 14, fontFamily: 'inherit', color: '#f1f5f9',
    transition: 'all 0.2s',
  },
  inputHint: { fontSize: 15, color: '#475569', paddingLeft: 4 },

  /* Topics step */
  topicStats: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 24,
  },
  topicCount: { fontSize: 14, color: '#38bdf8', fontWeight: 600 },
  selectAllBtn: {
    background: 'none', border: '1px solid #1e3a5f', color: '#64748b',
    borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
  },
  topicGroup: { marginBottom: 28 },
  topicGroupHeader: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
  },
  groupDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  groupLabel: { fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 0.8, textTransform: 'uppercase', flex: 1 },
  groupSelectBtn: {
    background: 'none', border: 'none', color: '#334155',
    cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 500,
  },
  topicRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  topicPill: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
    border: 'none',
  },

  /* Platforms step */
  platformGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
  },
  platformCard: {
    borderRadius: 16, padding: '24px 20px', textAlign: 'left',
    fontFamily: 'inherit', position: 'relative',
  },
  platformTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  platformIcon: { fontSize: 28 },
  platformCheck: {
    width: 22, height: 22, borderRadius: '50%',
    background: '#0ea5e9', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700,
  },
  soonBadge: {
    background: '#1e3a5f', color: '#475569',
    borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700,
  },
  platformName: { fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 },
  platformDesc: { fontSize: 13, color: '#475569', lineHeight: 1.55 },

  /* Time step */
  timeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  timeCard: {
    borderRadius: 14, padding: '18px 14px', textAlign: 'center',
    fontFamily: 'inherit', cursor: 'pointer', position: 'relative',
    overflow: 'hidden',
  },
  popularBadge: {
    position: 'absolute', top: 8, right: 8,
    fontSize: 9, color: '#f59e0b', fontWeight: 700,
  },
  timeCheck: {
    position: 'absolute', top: 8, left: 8,
    width: 16, height: 16, borderRadius: '50%',
    background: '#0ea5e9', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 9, fontWeight: 700,
  },
  timeIcon: { fontSize: 24, marginBottom: 8 },
  timeLabel: { fontSize: 14, fontWeight: 700, marginBottom: 4 },
  timeDesc: { fontSize: 11, color: '#475569' },
  customTimeWrap: { marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  customTimeLabel: { fontSize: 13, color: '#64748b', fontWeight: 600 },
  timeInput: {
    padding: '12px 16px', fontSize: 16,
    background: '#0a1628', border: '1.5px solid #1e3a5f',
    borderRadius: 10, fontFamily: 'inherit', color: '#f1f5f9',
    width: 180,
  },
  timezoneNote: {
    marginTop: 20, fontSize: 12, color: '#334155',
    background: '#0a1628', borderRadius: 8, padding: '10px 14px',
    lineHeight: 1.6,
  },
  tzLabel: { fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', marginBottom: 12 },
  tzGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 },
  tzBtn: {
    borderRadius: 10, padding: '10px 8px', cursor: 'pointer',
    fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    transition: 'all 0.15s',
  },
  otpInfoBox: {
    background: 'rgba(14,165,233,0.07)', border: '1px solid rgba(14,165,233,0.18)',
    borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8,
  },
  otpInfoIcon: { fontSize: 28, flexShrink: 0 },
  otpInfoTitle: { fontSize: 14, color: '#94a3b8', fontWeight: 600, marginBottom: 4 },
  otpInfoSub: { fontSize: 12, color: '#475569' },

  /* Phone step */
  phoneStep: { display: 'flex', flexDirection: 'column', gap: 24 },
  summaryCard: {
    background: '#0a1628', border: '1px solid #1e3a5f',
    borderRadius: 16, padding: '20px 24px',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  summaryRow: { display: 'flex', alignItems: 'center', gap: 14 },
  summaryIcon: { fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0 },
  summaryVal: { fontSize: 14, color: '#94a3b8', lineHeight: 1.5 },
  phoneLabel: { fontSize: 13, fontWeight: 600, color: '#64748b' },
  phoneHint: { fontSize: 12, color: '#334155' },
  errorBox: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 10, padding: '12px 16px', color: '#f87171', fontSize: 14,
  },
  submitBtn: {
    width: '100%', padding: '16px', fontSize: 17, fontWeight: 700,
    background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
    color: '#fff', border: 'none', borderRadius: 14,
    fontFamily: 'inherit', cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(14,165,233,0.3)',
  },
  legal: { fontSize: 11, color: '#1e3a5f', textAlign: 'center', lineHeight: 1.7 },

  /* Bottom bar */
  bottomBar: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    background: 'rgba(6,13,31,0.95)',
    backdropFilter: 'blur(16px)',
    borderTop: '1px solid #0d1829',
    padding: '16px 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    zIndex: 50,
  },
  selectedPreview: { display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1, overflow: 'hidden' },
  previewPill: {
    border: '1px solid', borderRadius: 999, padding: '3px 10px',
    fontSize: 11, fontWeight: 500,
  },
  moreCount: { fontSize: 11, color: '#334155', alignSelf: 'center' },
  nextBtn: {
    padding: '12px 32px', fontSize: 15, fontWeight: 700,
    background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
    color: '#fff', border: 'none', borderRadius: 12,
    fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(14,165,233,0.3)',
    whiteSpace: 'nowrap', flexShrink: 0,
  },
};
